import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tab, Typography } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useApiGet } from '../../../utils/httpClientTs';
import { getIRSFormSections } from '../../../endpoints/irsForms';
import IRSFormSection from './IRSFormSection';
import IRSFormSummary from './IRSFormSummary';
import { IRSFormDetail } from '../../../types/irsForms';

interface IRSFormSectionsProps {
  formId: string;
  formDetail: IRSFormDetail;
  editable: boolean;
  formType: 'lc' | 'sc' | 'rg';
  onApprove?: () => void;
  onReject?: () => void;
}

const IRSFormSections: React.FC<IRSFormSectionsProps> = ({
  formId,
  formDetail,
  editable,
  formType,
  onApprove,
  onReject,
}) => {
  const [value, setValue] = useState<string>('');

  const { data: sectionsData, isLoading: sectionsLoading } = useApiGet(
    ['irs-form-sections', formId],
    () => getIRSFormSections(formId),
    {
      enabled: !!formId,
      retry: 1,
    }
  );

  const sections = useMemo(() => sectionsData || [], [sectionsData]);

  useEffect(() => {
    // Set initial tab to first section
    if (sections.length > 0 && !value) {
      setValue(sections[0].id);
    }
  }, [sections, value]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  if (sectionsLoading) {
    return <Box>Loading sections...</Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            aria-label="IRS form sections"
            TabIndicatorProps={{
              sx: { height: '6px' },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sections.map((section) => (
              <Tab key={section.id} label={section.name} value={section.id} />
            ))}
            <Tab label="Summary" value="summary" />
          </TabList>
        </Box>
        {sections.map((section) => (
          <TabPanel key={section.id} value={section.id}>
            {/* Display dependency questions if any */}
            {section.dependency_questions &&
              section.dependency_questions.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Information from Other Sections
                  </Typography>
                  {section.dependency_questions.map((depQuestion) => (
                    <Box key={depQuestion.question_id} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {depQuestion.question_number || ''}.{' '}
                        {depQuestion.question_text}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 2, color: 'text.secondary' }}
                      >
                        {depQuestion.decimal_answer !== null &&
                        depQuestion.decimal_answer !== undefined
                          ? `$${depQuestion.decimal_answer.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : depQuestion.integer_answer !== null &&
                              depQuestion.integer_answer !== undefined
                            ? depQuestion.integer_answer.toLocaleString()
                            : depQuestion.text_answer
                              ? depQuestion.text_answer
                              : depQuestion.boolean_answer !== null &&
                                  depQuestion.boolean_answer !== undefined
                                ? depQuestion.boolean_answer
                                  ? 'Yes'
                                  : 'No'
                                : depQuestion.date_answer
                                  ? new Date(
                                      depQuestion.date_answer
                                    ).toLocaleDateString()
                                  : depQuestion.document_id
                                    ? 'Document attached'
                                    : 'Not answered'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            <IRSFormSection
              formId={formId}
              sectionId={section.id}
              editable={editable}
              formType={formType}
            />
          </TabPanel>
        ))}
        <TabPanel value="summary">
          <IRSFormSummary
            formDetail={formDetail}
            editable={editable}
            onApprove={onApprove}
            onReject={onReject}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default IRSFormSections;
